import React, { useEffect } from 'react';

import { observer } from 'mobx-react-lite';
import { ChatRoomType } from 'stores/chat_room_store';
import useStores from 'hooks/useStores';
import useUserIdentity from 'hooks/useUserIdentity';
import useSocketIO from 'hooks/useSocketIO';
import Error from 'components/Error';

type ChatRoomContextType = {

    createChatRoom: (
        identity: string,
        name: string,
        description: string,
        visibility: string,
    ) => Promise<ChatRoomType>;

    fetchList: (identity: string) => Promise<ChatRoomType[]>;

    // Fetch and get one
    fetchChatRoom: (identity: string, uuid: string) => Promise<ChatRoomType | null>;
    getChatRoom: (uuid: string) => ChatRoomType | null;

    // Contains all known ChatRoom items
    items: ChatRoomType[];
}

export const ChatRoomContext = React.createContext<ChatRoomContextType>({
    createChatRoom: () => { throw 'Not Implemented' },
    fetchList: () => { throw 'Not Implemented' },
    fetchChatRoom: () => { throw 'Not Implemented' },
    getChatRoom: () => { throw 'Not Implemented' },
    items: [] as ChatRoomType[],
});



export const ChatRoomProvider = observer(({ children }: { children: React.ReactNode }) => {

    const { chatRoomStore } = useStores();
    const { currentIdentity } = useUserIdentity();
    const { socketEmit } = useSocketIO();
    const { connectionState } = useSocketIO();

    useEffect(() => {

        if(connectionState !== 'connected') {
            return;
        }

        if (currentIdentity?.uuid && currentIdentity?.team) {
            chatRoomStore.fetchList(currentIdentity.uuid);
        }
    }, [currentIdentity, connectionState]);

    useEffect(() => {
        console.log(`Joining ${chatRoomStore.items.length} chat rooms...`);
        if(!socketEmit) {
            Error("There was an error connecting to the chat room")
            return;
        }
        chatRoomStore.items.forEach((item) => {
            console.log(`Joining chat room ${item.uuid}`);
            socketEmit('join', { channel: `chat_room/${item.uuid}`});
        });

    }, [chatRoomStore.items.length]);

    const fetchChatRoom = async (uuid: string) => {
        return chatRoomStore.fetchChatRoom(uuid);
    };

    const getChatRoom = (uuid: string) => {
        return chatRoomStore.items.find((item) => item.uuid === uuid) || null;
    };

    const fetchList = async (team: string) => {
        return chatRoomStore.fetchList(team)
            .then(() => {
                return chatRoomStore.items;
            });
    };

    const createChatRoom = async (
        identity: string,
        name: string,
        description: string,
        visibility: string,
    ) => {

        return chatRoomStore.createChatRoom(identity, name, description, visibility)
            .then((newChatRoom) => {
                return newChatRoom;
            });
    };


    const data = {
        createChatRoom,
        fetchList,
        fetchChatRoom,
        getChatRoom,
        items: chatRoomStore.items,
    };

    return (
        <ChatRoomContext.Provider value={data}>{ children }</ChatRoomContext.Provider>
    );

});
